import { AppointmentV2 } from "./appointment";
import { Tag } from "./tag";

export interface EventAPIResponse {
  id: number;
  appointment: Appointment;
  event_details?: EventDetails;
  technician?: Technician;
  created_by: CreatedBy;
  links: Links;
  is_ghost?: boolean;
  ghost_technician?: Technician;
}
export interface Appointment {
  id: number;
  priority: string;
  status: string;
  service_category_id: number;
  service_category_name: string;
  address: Address;
  customer: Customer;
  estimated_duration_minutes: number;
  estimated_end_time: string;
  arrival_window_start_time: string;
  arrival_window_end_time: string;
  pre_work_start_time?: null;
  pre_work_end_time: string;
  travel_start_time?: null;
  travel_end_time?: null;
  late: boolean;
  work_start_time: string;
  work_end_time: string;
  job_id: number;
  job_invoice_revenue_cents: number;
  job_status: JobStatus | null;
  ghost: boolean;
  assignment_pinned: boolean;
  links: Links1;
  unassigned: boolean;
  display_status?: string;
  ghost_scheduled_time?: string;
  job_tags?: Tag[];
}
export interface JobStatus {
  color: string;
  created_at: string;
  description: string;
  final: boolean;
  id: number;
  key: string;
  name: string;
  position: number;
  updated_at: string;
}
export interface Address {
  id: number;
  street: string;
  unit: string;
  city: string;
  state: string;
  zip_code: string;
  time_zone: string;
  name: string;
  primary: boolean;
  invalid: boolean;
  latitude: number;
  longitude: number;
  location_type: string;
  commercial_property: boolean;
  tax_exempt: boolean;
  links: Links2;
}
export interface Links2 {
  job_create: string;
  equipment_create: string;
}
export interface Customer {
  id: number;
  full_name: string;
  membership: Membership;
  phone: string;
  tags?: Tag[];
}
export interface Membership {
  membership_id: number;
  membership_program_id: number;
  membership_program_name: string;
  membership_program_description: string;
  starts_at: string;
  ends_at?: null;
  renews_at: string;
  covered_systems?: null;
  price_cents: number;
  active: boolean;
  auto_renews: boolean;
  duration: Duration;
  items?: null[] | null;
  icon_url: string;
  membership_update_url: string;
}
export interface Duration {
  period: string;
  count: number;
}
export interface Links1 {
  get: string;
}
export interface Technician {
  id: number;
  full_name: string;
  avatar: Avatar;
  department: Department;
  is_clocked_in: boolean;
}
export interface CreatedBy {
  id: number;
  full_name: string;
  user_type: string;
}
export interface Links {
  update: string;
}

export interface ResourceAPIResponse {
  id: number;
  avatar: Avatar;
  is_clocked_in: boolean;
  full_name: string;
  skill_level: string;
  installer: boolean;
  department: Department;
  business_hours: [{ dow: number; start_time: string; end_time: string }];
  show_path: string;
  belongs_to_departments: number[];
  user_id: number;
}
export interface Avatar {
  url: string;
}
export interface Department {
  id: number;
  name: string;
  initials: string;
}

export class TimelineEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  resource: number;
  ghost: boolean;
  editable = false;
  details: EventAPIResponse;
  constructor(params: {
    id: number;
    title: string;
    start: string;
    end: string;
    resource: number;
    ghost: boolean;
    editable?: boolean;
    details: EventAPIResponse;
  }) {
    Object.assign(this, params);
  }
}

export class TimelineResourceGroup {
  id: number;
  name: string;
  collapsed: boolean;
  eventCreation: boolean;
  children: TimelineResource[];
  fixed?: boolean;
  reorder = false;
  constructor(params: {
    id: number;
    name: string;
    collapsed: boolean;
    eventCreation: boolean;
    children: TimelineResource[];
  }) {
    Object.assign(this, params);
  }
}

export class TimelineResource {
  id: number;
  color?: string;
  name: string;
  fixed = false;
  reorder = false;
  cssClass?: string;
  constructor(params: {
    id: number;
    color?: string;
    name: string;
    fixed?: boolean;
  }) {
    Object.assign(this, params);
  }
}

export interface AppointmentEvent {
  allDay: boolean;
  allDayText: string;
  ariaLabel: string;
  bufferEnd?: null;
  bufferStart?: null;
  color: string;
  currentResource: CurrentResource;
  currentSlot: CurrentSlot;
  date: number;
  end: string;
  endDate: string;
  html: string;
  id: number;
  isMultiDay: boolean;
  lastDay: string;
  original: Original;
  position: Position;
  resource: number;
  start: string;
  startDate: string;
  style: Style;
  title: string;
  tooltip: string;
  uid: number;
  key: string;
  layoutStart: number;
  layoutEnd: number;
  offset: number;
  bufferAfter: string;
  bufferBefore: string;
  track: number;
}
export interface CurrentResource {
  id: number;
  color: string;
  name: string;
  reorder: boolean;
  depth: number;
  isParent: boolean;
}
export interface CurrentSlot {
  id: string;
}
export interface Original {
  id: number;
  title: string;
  start: string;
  end: string;
  resource: number;
  editable: boolean;
  ghost: boolean;
  details: EventAPIResponse;
}
export interface Position {
  height: string;
  left: string;
  right: string;
  width: string;
  top: number;
}
export interface Style {
  background: string;
  color: string;
}
export interface EventDetails {
  id: number;
  starts_at: string;
  ends_at: string;
  name: string;
  owner_type: string;
  owner_id: number;
  all_day: boolean;
  exclusive: boolean;
  participants_attributes?: ParticipantsAttributesEntity[] | null;
  recurring: boolean;
  recurrence_preset: string;
  recurrence_rules?: RecurrenceRulesEntity[] | null;
  technicians?: Technician[];
}
export interface ParticipantsAttributesEntity {
  participant_type: string;
  participant_id: number;
}
export interface RecurrenceRulesEntity {
  type: string;
  interval: number;
  ends: string;
}

export class AppointmentAdapter {
  constructor() {}
  toSchedulerAppointment(v2: AppointmentV2): Appointment {
    return {
      id: v2.id,
      priority: v2.service_category?.priority || "normal",
      status: v2.status,
      service_category_id: v2.service_category?.id ?? 0,
      service_category_name: v2.service_category?.name ?? "",
      address: v2.address,
      customer: {
        customer_id: v2.customer.customer_id,
        type: v2.customer.type,
        full_name: v2.customer.full_name,
        first_name: v2.customer.first_name,
        last_name: v2.customer.last_name,
        email: v2.customer.email,
        phone: v2.customer.phone,
        accounting_key: v2.customer.accounting_key,
        authentication_token: v2.customer.authentication_token,
        cable_token: v2.customer.cable_token,
        primary_address: v2.customer.primary_address,
        tags: v2.customer.tags,
        membership: v2.customer.membership
      },
      estimated_duration_minutes: v2.estimated_duration_minutes,
      arrival_window_start_time: v2.arrival_window_start,
      arrival_window_end_time: v2.arrival_window_end,
      pre_work_start_time: v2.accepted_time ?? "",
      pre_work_end_time: v2.dispatch_time ?? "",
      travel_start_time: v2.dispatch_time ?? "",
      travel_end_time: v2.arrival_time ?? "",
      late: this.isLate(v2),
      work_start_time: v2.arrival_time ?? "",
      work_end_time: v2.departure_time ?? "",
      job_id: v2.job?.id ?? 0,
      job_invoice_revenue_cents: v2.job?.invoice_revenue_cents ?? 0,
      job_status: v2.job?.status ?? null,
      ghost: v2.ghost ?? false,
      assignment_pinned: v2.assignment_pinned ?? false,
      links: v2.links,
      unassigned: !v2.technician,
      display_status: v2.display_status
    };
  }

  private isLate(v2: any): boolean {
    if (!v2.arrival_time || !v2.arrival_window_end) return false;
    const arrival = new Date(v2.arrival_time).getTime();
    const windowEnd = new Date(v2.arrival_window_end).getTime();
    return arrival > windowEnd;
  }
}

export class TimelineStep {
  title: string;
  time: string;
  date: string;
  duration: string;
  durationLabel: string;
  active?: boolean;
  completed?: boolean;
  circleFill: string;
  circleBorder: string;
  lineColor: string;
  iconColor: string;
  customIcon: string;
  grey?: boolean;
  editable: boolean;
  editableFieldName: string;
  lost?: boolean;

  constructor(params: Partial<TimelineStep> = {}) {
    Object.assign(this, params);
    if (params.grey === undefined) {
      this.grey = false;
    }
  }
}
